import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { ForwardsDetails, ForwardsGeneral } from '../../sj-orm/models/assets/categories';
import {
  ForwardOtherSideOfContract,
  ForwardsBasicType,
  ForwardsType,
  Seller,
} from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type Forwards = ForwardsFormGeneralType &
  ForwardsFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

const BasicAssetList = [
  {
    label: 'forms_assets:futures.enums.basicAssetType.commodity',
    key: ForwardsBasicType.commodity,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.equity',
    key: ForwardsBasicType.equity,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.currency',
    key: ForwardsBasicType.currency,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.cryptocurrency',
    key: ForwardsBasicType.cryptocurrency,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.bonds',
    key: ForwardsBasicType.bonds,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.index',
    key: ForwardsBasicType.index,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.interestRate',
    key: ForwardsBasicType.interestRate,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.futuresContract',
    key: ForwardsBasicType.futuresContract,
  },
  {
    label: 'forms_assets:futures.enums.basicAssetType.other',
    key: ForwardsBasicType.other,
  },
];

export const ForwardsList = [
  {
    label: 'forms_assets:forwards.enums.type.deliveryBased',
    key: ForwardsType.deliveryBased,
  },
  {
    label: 'forms_assets:forwards.enums.type.cashSettled',
    key: ForwardsType.cashSettled,
  },
];

const SellerList = [
  {
    label: 'forms_assets:forwards.enums.seller.seller',
    key: Seller.seller,
  },
  {
    label: 'forms_assets:forwards.enums.seller.buyer',
    key: Seller.buyer,
  },
];
const OtherSideOfContractList = [
  {
    label: 'forms_assets:forwards.enums.otherSideOfContract.legalEntity',
    key: ForwardOtherSideOfContract.legalEntity,
  },
  {
    label: 'forms_assets:forwards.enums.otherSideOfContract.person',
    key: ForwardOtherSideOfContract.person,
  },
];

type ForwardsHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.TEXT;
  type: QuestionType.SELECT_FROM_LIST;
  amount: QuestionType.NUMBER;
  purchaseValue: QuestionType.ASSET_MARKET_VALUE_CALCULATOR;
  basicAssetName: QuestionType.TEXT;
  basicAssetType: QuestionType.SELECT_FROM_LIST;
  otherBasicAssetType?: QuestionType.TEXT;
  basicAssetAmount: QuestionType.NUMBER;
  basicAssetHolder: QuestionType.TEXT;
};

type ForwardsHelpDetails = {
  expirationDate?: QuestionType.DATE_TIME;
  seller?: QuestionType.SELECT_FROM_LIST;
  otherSideOfContract?: QuestionType.SELECT_FROM_LIST;
  buyerSellerComment?: QuestionType.TEXT_MULTILINE;
  legalEntityName?: QuestionType.TEXT;
  personFullName?: QuestionType.TEXT;
  dealReserves?: QuestionType.CHECKBOX;
  dealReservesData?: QuestionType.TEXT;
  depositoryStatements?: QuestionType.PICK_ARRAY_OF_DTO;
  strategy?: QuestionType.TEXT;
  attachments?: QuestionType.FILE_UPLOAD;
};

type ForwardsFormGeneralType = TDtoWrapper<ForwardsGeneral, ForwardsHelpGeneral>;
type ForwardsFormDetailsType = TDtoWrapper<ForwardsDetails, ForwardsHelpDetails>;

const ForwardsFormGeneral: ForwardsFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:forwards.inputs.type.label',
    tooltip: 'forms_assets:forwards.inputs.type.tooltip',
    value: ForwardsType.cashSettled,
    items: ForwardsList,
    required: true,
  },
  amount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:forwards.inputs.amount.label',
    required: true,
    value: 0,
  },
  purchaseValue: {
    type: QuestionType.ASSET_MARKET_VALUE_CALCULATOR,
    label: 'forms_assets:forwards.inputs.purchaseValue.label',
    tooltip: 'forms_assets:forwards.inputs.purchaseValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  basicAssetType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:forwards.inputs.basicAssetType.label',
    tooltip: 'forms_assets:forwards.inputs.basicAssetType.tooltip',
    items: BasicAssetList,
    value: ForwardsBasicType.commodity,
  },
  otherBasicAssetType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:forwards.inputs.otherBasicAssetType.label',
    value: '',
    showIf: {
      basicAssetType: {
        value: ForwardsBasicType.other,
      },
    },
  },
  basicAssetName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:forwards.inputs.basicAssetName.label',
    value: '',
  },
  basicAssetAmount: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:forwards.inputs.basicAssetAmount.label',
    value: 0,
  },
  basicAssetHolder: {
    type: QuestionType.TEXT,
    label: 'forms_assets:forwards.inputs.basicAssetHolder.label',
    tooltip: 'forms_assets:forwards.inputs.basicAssetHolder.tooltip',
    value: '',
  },
};

const ForwardsFormDetails: ForwardsFormDetailsType = {
  otherSideLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:forwards.inputs.otherSideLabel.label',
    order: 1,
  },
  expirationDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:forwards.inputs.expirationDate.label',
    tooltip: 'forms_assets:forwards.inputs.expirationDate.tooltip',
    order: 7,
  },
  seller: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:forwards.inputs.seller.label',
    items: SellerList,
    order: 2,
  },
  otherSideOfContract: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:forwards.inputs.otherSideOfContract.label',
    items: OtherSideOfContractList,
    order: 3,
  },
  buyerSellerComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:forwards.inputs.buyerSellerComment.label',
    order: 5,
  },
  legalEntityName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.legalEntityName.label',
    showIf: {
      otherSideOfContract: {
        value: ForwardOtherSideOfContract.legalEntity,
      },
    },
    order: 4,
  },
  personFullName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:otcOptions.inputs.personFullName.label',
    showIf: {
      otherSideOfContract: {
        value: ForwardOtherSideOfContract.person,
      },
    },
    order: 4,
  },
  depositoryStatements: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:forwards.inputs.depositoryStatements.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    order: 8,
  },
  dealReserves: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:forwards.inputs.dealReserves.label',
    tooltip: 'forms_assets:forwards.inputs.dealReserves.tooltip',
    order: 10,
  },
  dealReservesData: {
    type: QuestionType.TEXT,
    label: 'forms_assets:forwards.inputs.dealReservesData.label',
    showIf: {
      dealReserves: {
        value: true,
      },
    },
    order: 11,
  },
  strategy: {
    type: QuestionType.TEXT,
    label: 'forms_assets:forwards.inputs.strategy.label',
    tooltip: 'forms_assets:forwards.inputs.strategy.tooltip',
    order: 9,
    colSpan: 2,
  },
  attachments: baseAssetFields.attachments,
};

const ForwardsForm = [
  ForwardsFormGeneral,
  ForwardsFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentWithBroker',
    'publicFundTaxAgent',
    'taxAgentOther',
    'taxAgentWithBrokerOther',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];
export { ForwardsForm };
